import { createStandaloneToast } from "@chakra-ui/react";
import { Override } from "@syla/shared/types/helpers/Override";
import {
  SyncState,
  PopulatedWallet,
} from "@syla/shared/types/models/WalletBase";
import { DataSourceData } from "@syla/shared/types/responses/TransactionEvent";
import { secondsToMilliseconds } from "date-fns";
import { errorToToastOptions } from "../../api/errors/errorToToastOptions";
import { syncWallet as syncWalletRequest } from "../../api/wallet/syncWallet";
import { buildSyncErrorToastProps } from "../../helper/wallet/buildSyncErrorToastProps";
import { theme } from "../../theme";
import { checkStatusAndDoPendingWork } from "../pendingWork";
import {
  queryClient,
  getAccountTransactionsCacheKey,
} from "../transactionsStore";
import { getAllAssetsCacheKey } from "../useQueryAssets";
import { buildSyncSuccessToastProps } from "./buildSyncSuccessToastProps";
import { updateCachedWalletState } from "./updateCachedWalletState";
import { getWalletsQueryCacheKey } from "./wallet";

export type WalletToSync = Override<
  Pick<PopulatedWallet, "_id" | "customName" | "dataSource">,
  {
    dataSource: Pick<DataSourceData, "name" | "syncAuthType">;
  }
>;

export const syncWallet = async (
  accountId: string,
  wallet: WalletToSync,
  fromDate?: Date,
): Promise<void> => {
  const toast = createStandaloneToast({ theme });

  updateCachedWalletState(accountId, [wallet._id], {
    syncState: SyncState.InProgress,
  });

  // trigger a refresh of wallet state after a while
  // give it time to initialise the state
  setTimeout(() => {
    queryClient.invalidateQueries([...getWalletsQueryCacheKey(accountId)]);
  }, secondsToMilliseconds(3));

  try {
    const response = await syncWalletRequest(accountId, wallet._id, fromDate);

    checkStatusAndDoPendingWork();

    if (response.failed.length === 0) {
      toast(buildSyncSuccessToastProps([wallet]));
    } else {
      toast(buildSyncErrorToastProps(response));
    }
  } catch (error) {
    toast(errorToToastOptions(error));
  } finally {
    queryClient.invalidateQueries([
      ...getAccountTransactionsCacheKey(accountId),
    ]);
    queryClient.invalidateQueries(getAllAssetsCacheKey());
  }
};
